/**
 * @docs: docs/core/architecture/SCOUT_GATEWAY_REMOTE.md
 *
 * The owner shares a view-only look at their SG (Tony, 2026-10-06): a link and
 * a PIN, sent by WhatsApp or email, for someone with no MyPolaris account. It
 * opens on one device, within 48 hours, and locks after 5 wrong PINs; that
 * device looks, never changes, until the end date or until the owner removes it.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShareIcon } from '@heroicons/react/24/outline';
import { api, type GatewayGuest, type GatewayGuestLink } from '../../services/api';
import { formatRelativeTime } from '../../utils/formatters';
import { useToastStore } from '../../store/toastStore';

const DAYS = [
  { days: 1, label: '1 day' }, { days: 3, label: '3 days' }, { days: 7, label: '1 week' },
  { days: 14, label: '2 weeks' }, { days: 31, label: '1 month' },
];

const day = (iso: string) => new Date(iso).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' });

/** The message the skipper sends: the link, the PIN, and what to expect. */
export function guestMessage(boat: string, link: GatewayGuestLink): string {
  return [
    `Here's a view-only look at ${boat}'s Scout Gateway: position, wind, depth and more.`,
    '',
    link.url,
    `PIN: ${link.pin}`,
    '',
    `Open it within 48 hours, on the device you'll use: it works on one device only. You can look until ${day(link.until)}.`,
  ].join('\n');
}

function state(g: GatewayGuest): { text: string; colour: string } {
  switch (g.state) {
    case 'open': return { text: `Opened ${formatRelativeTime(g.opened_at!)} · until ${day(g.until)}`, colour: 'text-green-400' };
    case 'waiting': return { text: `Not opened yet · link works until ${day(g.link_expires)}`, colour: 'text-slate-400' };
    case 'locked': return { text: 'Locked after 5 wrong PINs', colour: 'text-amber-300' };
    case 'expired': return { text: 'Link not opened in time', colour: 'text-slate-500' };
    case 'ended': return { text: `Ended ${day(g.until)}`, colour: 'text-slate-500' };
  }
}

function Made({ boat, link, onDone }: { boat: string; link: GatewayGuestLink; onDone: () => void }) {
  const text = guestMessage(boat, link);
  const toast = useToastStore.getState().addToast;
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast({ type: 'success', title: 'Copied', message: 'Paste it into a message.' }); }
    catch { toast({ type: 'error', title: 'Not copied', message: 'Select the message and copy it.' }); }
  };
  const share = 'px-3 py-2 rounded-lg text-sm font-medium text-white inline-flex items-center justify-center gap-1.5';
  return (
    <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 p-3 space-y-3" data-testid="guest-made">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-white">Link for {link.name}</p>
        <p className="text-sm text-slate-300">PIN <span className="font-mono text-lg text-cyan-300 tracking-widest">{link.pin}</span></p>
      </div>
      <pre className="whitespace-pre-wrap break-all text-xs text-slate-300 bg-slate-900/60 rounded p-2 font-sans select-all">{text}</pre>
      <div className="grid grid-cols-3 gap-2">
        <a className={`${share} bg-green-600 hover:bg-green-500`} target="_blank" rel="noopener"
          href={`https://wa.me/?text=${encodeURIComponent(text)}`}>WhatsApp</a>
        <a className={`${share} bg-slate-600 hover:bg-slate-500`}
          href={`mailto:?subject=${encodeURIComponent(`${boat}: a look at the boat`)}&body=${encodeURIComponent(text)}`}>Email</a>
        <button type="button" onClick={copy} className={`${share} bg-slate-600 hover:bg-slate-500`}>Copy</button>
      </div>
      <p className="text-xs text-slate-400">Anyone with this message can open it once. They can look, never change anything.</p>
      <button type="button" onClick={onDone} className="text-sm text-slate-300 hover:text-white">Done</button>
    </div>
  );
}

export function SgGuestLinks({ deviceId, boat }: { deviceId: string; boat: string }) {
  const qc = useQueryClient();
  const key = ['gateway-guests', deviceId];
  const { data, isError } = useQuery({ queryKey: key, queryFn: () => api.getGatewayGuests(deviceId), retry: false });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [days, setDays] = useState(7);
  const [made, setMade] = useState<GatewayGuestLink | null>(null);
  const toast = useToastStore.getState().addToast;
  const create = useMutation({
    mutationFn: () => api.createGatewayGuest(deviceId, { name: name.trim(), days }),
    onSuccess: (link) => { setMade(link); setOpen(false); setName(''); qc.invalidateQueries({ queryKey: key }); },
    onError: (e: unknown) => toast({ type: 'error', title: 'No link made',
      message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Try again in a moment.' }),
  });
  const remove = useMutation({
    mutationFn: (guestId: string) => api.removeGatewayGuest(deviceId, guestId),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
  if (isError) return null;
  const guests = data?.guests ?? [];

  return (
    <div className="bg-slate-700/50 rounded-lg p-3 space-y-3" data-testid="sg-guests">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm text-slate-400">Share a view-only look</p>
          <p className="text-sm text-slate-300 mt-0.5">A link and PIN by WhatsApp or email. No account needed.</p>
        </div>
        {!open && !made && (
          <button type="button" onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium">
            <ShareIcon className="w-4 h-4" /> Share
          </button>
        )}
      </div>

      {open && (
        <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); if (name.trim()) create.mutate(); }}>
          <label className="flex flex-col text-sm text-slate-300 grow min-w-[10rem]">
            Who is it for?
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="e.g. Mum, or the marina"
              className="mt-1 bg-slate-900 border border-slate-600 rounded px-2 py-1.5 text-white" autoFocus />
          </label>
          <label className="flex flex-col text-sm text-slate-300">
            For
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}
              className="mt-1 bg-slate-900 border border-slate-600 rounded px-2 py-1.5 text-white">
              {DAYS.map((d) => <option key={d.days} value={d.days}>{d.label}</option>)}
            </select>
          </label>
          <button type="submit" disabled={!name.trim() || create.isPending}
            className="px-4 py-1.5 rounded bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white text-sm font-medium">
            {create.isPending ? 'Making…' : 'Make link'}
          </button>
          <button type="button" onClick={() => setOpen(false)} className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-white text-sm">Cancel</button>
        </form>
      )}

      {made && <Made boat={boat} link={made} onDone={() => setMade(null)} />}

      {guests.length > 0 && (
        <ul className="divide-y divide-slate-600/60">
          {guests.map((g) => {
            const s = state(g);
            return (
              <li key={g.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div className="min-w-0">
                  <p className="text-white">{g.name} <span className="text-xs text-slate-400">· guest, looks only</span></p>
                  <p className={`text-sm ${s.colour}`}>{s.text}</p>
                </div>
                {g.state !== 'ended' && g.state !== 'expired' && (
                  <button onClick={() => remove.mutate(g.id)} className="px-3 py-1 rounded bg-slate-700 hover:bg-red-700 text-white text-sm">Remove</button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
